import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./CallSummary.module.css";
import Loader2 from "../../Loader2/Loader2";
import { fetchAgentDetailById, getAgentCallById } from "../../../Store/apiStore";
import { useAgentStore } from "../../../Store/agentDetailStore";
import { trackEvent } from "../../../lib/analytics";
import AnimatedButton from "../../AnimatedButton/AnimatedButton";

/*
 * CallSummary — Variant B, post-call screen.
 *
 *   /agent-live → user apne phone se call karta hai → call >= 30 sec
 *      → /call-summary   ← YE SCREEN
 *   (30 sec se chhoti call seedha /21-day-trial pe jaati hai)
 *
 * Maqsad: user ko dikhana ki uski ASLI call me agent ne kya-kya pakda —
 * caller ka naam, sentiment, phone/email/address/reason, recording aur
 * transcript. Yahi proof usse "Activate" tak le jaata hai.
 *
 * Data wahi rasta use karta hai jo CallDetails use karta hai
 * (getAgentCallById), taaki fields ek jaisi rahein. callId/agentId
 * navigation state se aate hain — socket ka phone_call_completed event ya
 * /agent-live ka app-kill fallback dono yahi bhejte hain.
 */

const AGENT_AVATAR = "/svg/receptionist_profile.png";
// Shuru me itne hi messages — poora transcript lamba ho sakta hai.
const PREVIEW_MESSAGES = 4;

const msToMinSec = (ms) => {
    const total = Math.max(0, Math.round(Number(ms || 0) / 1000));
    const m = String(Math.floor(total / 60)).padStart(2, "0");
    const s = String(total % 60).padStart(2, "0");
    return `${m}:${s} min`;
};

const formatAudioTime = (seconds) => {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    const minutes = String(Math.floor(total / 60)).padStart(2, "0");
    const secs = String(total % 60).padStart(2, "0");
    return `${minutes}:${secs}`;
};

// start_timestamp Retell se ms me aata hai, kabhi ISO string bhi.
const toDate = (ts) => {
    const n = Number(ts);
    const d = Number.isFinite(n) && n > 0 ? new Date(n) : new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
};

const dayLabel = (d) => {
    if (!d) return "-";
    const today = new Date();
    const sameDay =
        d.getDate() === today.getDate() &&
        d.getMonth() === today.getMonth() &&
        d.getFullYear() === today.getFullYear();
    return sameDay
        ? "Today"
        : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const timeLabel = (d) =>
    d
        ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true })
        : "-";

const CallSummary = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { agentData } = useAgentStore();

    // Socket event / app-kill fallback dono state me callId + agentId bhejte
    // hain. Reload par state udd jaati hai, isliye sessionStorage ka agentId
    // fallback rakha hai.
    const callId = location.state?.callId || location.state?.call?.call_id || "";
    const agentId =
        location.state?.agentId || sessionStorage.getItem("SelectAgentId") || "";
    const startTs =
        location.state?.call?.start_timestamp || location.state?.start_timestamp || "";

    const [callData, setCallData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);
    const audioRef = useRef(null);
    const [audioCurrentTime, setAudioCurrentTime] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    // Store khaali ho sakta hai (socket ne yahan seedha bheja, ya user ne reload
    // kiya) — us soorat me naam "Sophia" pe gir jaata tha jabki asli agent koi
    // aur hai. Isliye agent details yahan khud fetch karte hain.
    const [fetchedAgentName, setFetchedAgentName] = useState("");

    const agentName = (
        fetchedAgentName ||
        agentData?.agent?.agentName ||
        "Sophia"
    ).trim();

    useEffect(() => {
        trackEvent("b_flow_call_summary_view", { screen: "call_summary" });
    }, []);

    useEffect(() => {
        if (!isTranscriptExpanded) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isTranscriptExpanded]);

    // Asli agent ka naam — heading, "What X Captured", "Answered by" aur
    // transcript ke labels sab isi pe chalte hain.
    useEffect(() => {
        if (!agentId) return undefined;
        if (agentData?.agent?.agentName) return undefined; // store me pehle se hai
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchAgentDetailById({
                    agentId,
                    bussinesId: sessionStorage.getItem("SelectAgentBusinessId"),
                });
                if (!cancelled && res?.data?.agent?.agentName) {
                    setFetchedAgentName(res.data.agent.agentName);
                }
            } catch (err) {
                console.error("[CallSummary] agent name fetch failed:", err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [agentId, agentData?.agent?.agentName]);

    /*
     * Call detail laao — thodi retry ke saath.
     *
     * Race hai: backend call_ended pe record file me likhta hai aur
     * call_analyzed pe use analysis se update karta hai. Hamara socket event
     * call_analyzed ke shuru me jaata hai, isliye kabhi-kabhi hum record ke
     * poori tarah settle hone se PEHLE hi maang lete hain aur 404 milta hai.
     *
     * Isliye do baar aur koshish karte hain. Phir bhi na mile to saaf error
     * dikhate hain — pehle ye chup-chaap khaali screen (sab "-") ban jaata tha,
     * jo dekhne me toota hua lagta hai.
     */
    useEffect(() => {
        if (!callId || !agentId) {
            setLoading(false);
            setLoadFailed(true);
            return undefined;
        }
        let cancelled = false;

        (async () => {
            for (let attempt = 0; attempt < 3 && !cancelled; attempt += 1) {
                try {
                    const res = await getAgentCallById(agentId, callId, startTs);
                    if (cancelled) return;
                    if (res?.call) {
                        setCallData(res.call);
                        setLoading(false);
                        return;
                    }
                } catch (err) {
                    console.error(
                        `[CallSummary] call fetch attempt ${attempt + 1} failed`,
                        { agentId, callId, startTs },
                        err
                    );
                }
                // Aakhri koshish ke baad rukna nahi hai.
                if (attempt < 2 && !cancelled) {
                    await new Promise((r) => setTimeout(r, 2000));
                }
            }
            if (!cancelled) {
                setLoading(false);
                setLoadFailed(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [callId, agentId, startTs]);

    const analysis = callData?.call_analysis || {};
    const captured = analysis.custom_analysis_data || {};

    const callerName =
        captured.name ||
        callData?.contact_name ||
        callData?.retell_llm_dynamic_variables?.contact_name ||
        "Unknown caller";

    const sentiment = analysis.user_sentiment || "";
    const sentimentClass =
        sentiment === "Positive"
            ? ""
            : sentiment === "Negative"
                ? styles.sentimentNegative
                : styles.sentimentNeutral;

    // phone_call pe asli telephony leg from_number hota hai; analysis wala
    // number tab collect kiya gaya number hai. CallDetails bhi yahi priority
    // use karta hai.
    const phone =
        callData?.call_type === "phone_call"
            ? callData?.from_number || captured.phone_number
            : captured.phone_number;

    const startedAt = useMemo(
        () => toDate(callData?.start_timestamp || startTs),
        [callData?.start_timestamp, startTs]
    );

    const transcript = callData?.transcript_object || [];
    const visible = showAll ? transcript : transcript.slice(0, PREVIEW_MESSAGES);

    const renderTranscriptMessages = (messages) =>
        messages.map((msg, i) => {
            const isAgent = msg.role === "agent";
            return (
                <div
                    key={`${i}-${msg.role}`}
                    className={`${styles.msgRow} ${isAgent ? "" : styles.msgRowUser}`}
                >
                    {isAgent && (
                        <img
                            src={AGENT_AVATAR}
                            alt=""
                            className={styles.msgAvatar}
                            aria-hidden="true"
                        />
                    )}
                    <div className={styles.msgCol}>
                        <div className={isAgent ? styles.bubbleAgent : styles.bubbleUser}>
                            {msg.content}
                        </div>
                        <span className={`${styles.msgWho} ${isAgent ? "" : styles.msgWhoRight}`}>
                            {isAgent ? agentName : "You"}
                        </span>
                    </div>
                </div>
            );
        });

    const capturedRows = [
        { label: "Phone number", value: phone },
        {
            label: "Email address",
            value: captured.email || callData?.retell_llm_dynamic_variables?.user_email,
        },
        { label: "Address", value: captured.address },
        { label: "Call Reason", value: captured.reason || captured.call_purpose },
    ];

    const handleActivate = () => {
        trackEvent("b_flow_activate_clicked", { screen: "call_summary" });
        navigate("/21-day-trial");
    };

    const handleAudioToggle = async () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (audio.paused) {
            try {
                await audio.play();
                setIsAudioPlaying(true);
            } catch (err) {
                console.error("[CallSummary] recording playback failed:", err);
            }
        } else {
            audio.pause();
            setIsAudioPlaying(false);
        }
    };

    const handleAudioSeek = (event) => {
        const audio = audioRef.current;
        const nextTime = Number(event.target.value);
        if (!audio || !Number.isFinite(nextTime)) return;
        audio.currentTime = nextTime;
        setAudioCurrentTime(nextTime);
    };

    if (loading) {
        return (
            <div className={styles.containerBox}>
                <div className={styles.loadingWrap}>
                    <Loader2 />
                </div>
            </div>
        );
    }

    // Call detail aayi hi nahi. Sab fields "-" dikhane se behtar hai saaf kehna,
    // aur user ko aage badhne ka rasta dena — wo yahan phanse nahi.
    if (loadFailed || !callData) {
        return (
            <div className={styles.containerBox}>
                <div className={styles.container}>
                    <h1 className={styles.pageTitle}>
                        {agentName} handled
                        <br />
                        your call
                    </h1>
                    <p className={styles.emptyNote}>
                        We couldn't load the details of that call right now — but{" "}
                        {agentName} is live and answering on your business number.
                    </p>
                </div>
                <div className={styles.footer}>
                    <AnimatedButton
                        label={
                            <span className={styles.ctaLabel}>
                                Activate {agentName} <span aria-hidden="true"> →</span>
                            </span>
                        }
                        onClick={handleActivate}
                        positionType="relative"
                        width="100%"
                        bottom="0"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.containerBox}>
            <div className={styles.container}>
                <h1 className={styles.pageTitle}>
                    Call captured successfully!
                </h1>
                <p className={styles.details}>Details below</p>

                <div className={styles.summaryCard}>
                    <div className={styles.callerRow}>
                        <div>
                            <span className={styles.callerLabel}>Caller Name</span>
                            <h2 className={styles.callerName}>{callerName}</h2>
                        </div>
                        {sentiment && (
                            <div className={styles.sentimentWrap}>
                                <span className={styles.sentimentLabel}>Sentiment</span>
                                <span className={`${styles.sentimentBadge} ${sentimentClass}`}>
                                    {sentiment}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className={styles.divder}></div>
                    <div className={styles.metaRow}>
                        <div className={styles.metaCell}>
                            <span className={styles.metaLabel}>{dayLabel(startedAt)}</span>
                            <span className={styles.metaValue}>{timeLabel(startedAt)}</span>
                        </div>
                        <div className={styles.metaCell}>
                            <span className={styles.metaLabel}>Answered by</span>
                            <span className={styles.metaValue}>{agentName.toUpperCase()}</span>
                        </div>
                        <div className={styles.metaCell}>
                            <span className={styles.metaLabel}>Duration</span>
                            <span className={styles.metaValue}>
                                {msToMinSec(callData?.duration_ms)}
                            </span>
                        </div>
                    </div>
                </div>
                <div className={styles.wraperdiv}>


                    <h3 className={styles.sectionTitle}>What {agentName} Captured</h3>
                    <ul className={styles.capturedList}>
                        {capturedRows.map((row) => (
                            <li key={row.label} className={styles.capturedRow}>
                                <span className={styles.capturedLabel}>{row.label}</span>
                                <span className={styles.capturedValue}>{row.value || "-"}</span>
                            </li>
                        ))}
                        {callData?.recording_url && (
                            <li className={styles.capturedRow}>
                                <span className={styles.capturedLabel}>Call Recording</span>
                                <span className={styles.audioWrap}>
                                    <audio
                                        className={styles.audio}
                                        preload="none"
                                        src={callData.recording_url}
                                        ref={audioRef}
                                        onLoadedMetadata={(event) =>
                                            setAudioDuration(event.currentTarget.duration)
                                        }
                                        onTimeUpdate={(event) =>
                                            setAudioCurrentTime(event.currentTarget.currentTime)
                                        }
                                        onPlay={() => setIsAudioPlaying(true)}
                                        onPause={() => setIsAudioPlaying(false)}
                                        onEnded={() => setIsAudioPlaying(false)}
                                    />
                                    <button
                                        type="button"
                                        className={styles.audioPlayButton}
                                        onClick={handleAudioToggle}
                                        aria-label={isAudioPlaying ? "Pause recording" : "Play recording"}
                                    >
                                        <span
                                            className={isAudioPlaying ? styles.audioPauseIcon : styles.audioPlayIcon}
                                            aria-hidden="true"
                                        />
                                    </button>
                                    <input
                                        type="range"
                                        className={styles.audioProgress}
                                        min="0"
                                        max={audioDuration || 0}
                                        step="0.1"
                                        value={Math.min(audioCurrentTime, audioDuration || 0)}
                                        onChange={handleAudioSeek}
                                        aria-label="Recording progress"
                                        style={{
                                            "--progress": `${audioDuration ? (audioCurrentTime / audioDuration) * 100 : 0}%`,
                                        }}
                                    />
                                    <span className={styles.audioTime}>
                                        {formatAudioTime(audioCurrentTime)}/{formatAudioTime(audioDuration)}
                                    </span>
                                </span>
                            </li>
                        )}
                    </ul>

                    {transcript.length > 0 && (
                        <>
                            <h3 className={styles.sectionTitle}>Call Transcript</h3>
                            <div className={styles.chatBox}>
                                <button
                                    type="button"
                                    className={styles.expandTranscriptButton}
                                    onClick={() => setIsTranscriptExpanded(true)}
                                    aria-label="Open transcript fullscreen"
                                >
                                    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17 2.5499V14.4492C17 15.8556 15.8566 16.9991 14.4502 16.9991H10.2008C9.73067 16.9991 9.35058 16.6182 9.35058 16.1489C9.35058 15.6795 9.73067 15.2986 10.2008 15.2986H14.4502C14.9195 15.2986 15.3004 14.9169 15.3004 14.4484L15.2996 2.5499C15.2996 2.08135 14.9187 1.69966 14.4494 1.69966L2.55057 1.70046C2.08204 1.70046 1.70116 2.08136 1.70116 2.54991V6.79948C1.70116 7.26882 1.32108 7.64972 0.850955 7.64972C0.380832 7.64972 0.000748205 7.26882 0.000748205 6.79948V2.54991C0.000748205 1.14348 1.14418 0 2.55055 0H14.4494C15.8557 0 17 1.14427 17 2.5499ZM7.65017 8.49996H3.40076C2.93064 8.49996 2.55056 8.88085 2.55056 9.3502C2.55056 9.81954 2.93064 10.2004 3.40076 10.2004H5.59829L0.249203 15.5497C-0.0830676 15.882 -0.0830676 16.4199 0.249203 16.7514C0.415738 16.9171 0.633265 17 0.850794 17C1.06832 17 1.28585 16.9171 1.45159 16.7506L6.80067 11.4021V13.5997C6.80067 14.0691 7.18076 14.45 7.65088 14.45C8.121 14.45 8.50109 14.0691 8.50109 13.5997V9.35014C8.50029 8.88079 8.12029 8.49996 7.65017 8.49996Z" fill="#6524EB" />
                                    </svg>

                                </button>
                                {renderTranscriptMessages(visible)}

                                {!showAll && transcript.length > PREVIEW_MESSAGES && (
                                    <button
                                        type="button"
                                        className={styles.showAll}
                                        onClick={() => setShowAll(true)}
                                    >
                                        Show Full Transcript
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
            {isTranscriptExpanded && (
                <div className={styles.transcriptOverlay} role="dialog" aria-modal="true" aria-label="Call transcript">
                    <div className={styles.transcriptModal}>
                        <div className={styles.transcriptModalHeader}>
                            <h2>Call Transcript</h2>
                            <button
                                type="button"
                                className={styles.closeTranscriptButton}
                                onClick={() => setIsTranscriptExpanded(false)}
                                aria-label="Close transcript"
                            >
                                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        <div className={`${styles.chatBox} ${styles.transcriptModalBody}`}>
                            {renderTranscriptMessages(transcript)}
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.footer}>
                <AnimatedButton
                    label={`Activate ${agentName} ->`}
                    onClick={handleActivate}
                    positionType="relative"
                    width="100%"
                    bottom="0"
                />
                <p className={styles.footerNote}>
                    <strong>{agentName}</strong> handled your call and captured the important
                    details automatically.
                </p>
            </div>
        </div>
    );
};

export default CallSummary;
